import {
  useEffect,
  useId,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { SearchInput } from '@/components/forms/search-input'
import { cn } from '@/lib/cn'

import type { OidcClaimSource } from './api'

type ClaimSourceOrigin = OidcClaimSource['origin']

const originOrder: ClaimSourceOrigin[] = [
  'account',
  'account_attribute',
  'organization',
  'organization_attribute',
]
const popupGap = 4
const popupMaxHeight = 360
const viewportPadding = 8

interface PopupPosition {
  style: CSSProperties
}

export function OidcClaimSourceSelect({
  id,
  invalid = false,
  onValueChange,
  sources,
  value,
}: {
  id: string
  invalid?: boolean
  onValueChange: (value: string) => void
  sources: OidcClaimSource[]
  value: string
}) {
  const { t } = useTranslation('adminIdp')
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const selectedSource = sources.find((source) => source.source === value)
  const sourceLabel = useCallback(
    (source: OidcClaimSource) =>
      t(`mappers.claimSources.${source.key}`, { defaultValue: source.label }),
    [t],
  )

  const filteredSources = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase()
    if (!normalizedQuery) return sources

    return sources.filter((source) =>
      [sourceLabel(source), source.label, source.source, t(`mappers.origins.${source.origin}`)]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    )
  }, [query, sourceLabel, sources, t])

  const sourceGroups = useMemo(
    () =>
      originOrder
        .map((origin) => ({
          origin,
          sources: filteredSources.filter((source) => source.origin === origin),
        }))
        .filter((group) => group.sources.length > 0),
    [filteredSources],
  )
  const displayedSources = useMemo(
    () => sourceGroups.flatMap((group) => group.sources),
    [sourceGroups],
  )

  useEffect(() => {
    if (!isOpen) return undefined

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    window.requestAnimationFrame(() =>
      popupRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus(),
    )
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    function updatePopupPosition() {
      const trigger = triggerRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const belowSpace = window.innerHeight - rect.bottom - popupGap - viewportPadding
      const aboveSpace = rect.top - popupGap - viewportPadding
      const openUpwards = belowSpace < Math.min(240, popupMaxHeight) && aboveSpace > belowSpace
      const availableHeight = openUpwards ? aboveSpace : belowSpace
      const maxHeight = Math.max(180, Math.min(popupMaxHeight, availableHeight))
      const width = Math.min(Math.max(rect.width, 420), window.innerWidth - viewportPadding * 2)
      const left = Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - width - viewportPadding),
      )

      setPopupPosition({
        style: {
          ...(openUpwards
            ? { bottom: window.innerHeight - rect.top + popupGap }
            : { top: rect.bottom + popupGap }),
          left,
          maxHeight,
          width,
        },
      })
    }

    updatePopupPosition()
    window.addEventListener('resize', updatePopupPosition)
    window.addEventListener('scroll', updatePopupPosition, true)
    return () => {
      window.removeEventListener('resize', updatePopupPosition)
      window.removeEventListener('scroll', updatePopupPosition, true)
    }
  }, [isOpen])

  function closeAndRestoreFocus() {
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  function openPicker() {
    setQuery('')
    setActiveIndex(0)
    setIsOpen(true)
  }

  function selectSource(source: OidcClaimSource) {
    onValueChange(source.source)
    closeAndRestoreFocus()
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeAndRestoreFocus()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => Math.min(current + 1, displayedSources.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
      return
    }
    if (event.key === 'Enter' && displayedSources[activeIndex]) {
      event.preventDefault()
      selectSource(displayedSources[activeIndex])
    }
  }

  return (
    <div className="relative h-[38px] w-full text-sm">
      <button
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={cn(
          'flex h-full w-full min-w-0 cursor-pointer items-center rounded-lg border bg-white pl-3 pr-9 text-left shadow-[var(--shadow-control)] transition-colors outline-none',
          'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)]',
          invalid
            ? 'border-[var(--color-danger)]'
            : 'border-[var(--color-control-border)] hover:border-[var(--color-control-border-hover)]',
        )}
        id={id}
        onClick={() => {
          if (isOpen) setIsOpen(false)
          else openPicker()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsOpen(false)
            return
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openPicker()
          }
        }}
        ref={triggerRef}
        role="combobox"
        type="button"
      >
        {selectedSource ? (
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate text-[var(--color-text)]">{sourceLabel(selectedSource)}</span>
            <span className="shrink-0 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)]">
              {t(`mappers.valueTypes.${selectedSource.value_type}`)}
            </span>
          </span>
        ) : (
          <span className="truncate text-[var(--color-control-placeholder)]">
            {t('mappers.placeholders.source')}
          </span>
        )}
      </button>
      <svg
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-3 -translate-y-1/2 text-[var(--color-text-muted)] transition-transform',
          isOpen ? 'rotate-180' : null,
        )}
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="m3 4.5 3 3 3-3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
      {isOpen && popupPosition
        ? createPortal(
            <div
              className="fixed z-[120] flex overflow-hidden rounded-lg border border-[var(--color-panel-border)] bg-white shadow-[0_12px_36px_rgba(26,28,25,0.16)]"
              ref={popupRef}
              style={popupPosition.style}
            >
              <div className="flex min-h-0 w-full flex-col">
                <div className="border-b border-[var(--color-divider)] p-2.5">
                  <SearchInput
                    aria-label={t('mappers.sourcePicker.searchLabel')}
                    onChange={(event) => {
                      setQuery(event.target.value)
                      setActiveIndex(0)
                    }}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={t('mappers.sourcePicker.searchPlaceholder')}
                    value={query}
                  />
                </div>
                <div className="min-h-0 overflow-y-auto py-1.5" id={listboxId} role="listbox">
                  {sourceGroups.length > 0 ? (
                    sourceGroups.map((group) => (
                      <div
                        aria-label={t(`mappers.origins.${group.origin}`)}
                        className="pb-1 last:pb-0"
                        key={group.origin}
                        role="group"
                      >
                        <div className="flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold tracking-wide text-[var(--color-text-muted)] uppercase">
                          <span>{t(`mappers.origins.${group.origin}`)}</span>
                          <span>{group.sources.length}</span>
                        </div>
                        {group.sources.map((source) => {
                          const sourceIndex = displayedSources.indexOf(source)
                          const selected = source.source === value
                          const active = sourceIndex === activeIndex
                          return (
                            <button
                              aria-selected={selected}
                              className={cn(
                                'flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left outline-none',
                                active ? 'bg-[var(--color-hover-muted)]' : null,
                                selected
                                  ? 'bg-[rgba(30,95,255,0.08)] text-[var(--color-primary)]'
                                  : 'text-[var(--color-text)]',
                              )}
                              key={source.source}
                              onClick={() => selectSource(source)}
                              onMouseEnter={() => setActiveIndex(sourceIndex)}
                              role="option"
                              type="button"
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">
                                  {sourceLabel(source)}
                                </span>
                                <code
                                  className="mt-0.5 block truncate text-[11px] leading-4 text-[var(--color-text-muted)]"
                                  title={source.source}
                                >
                                  {source.source}
                                </code>
                              </span>
                              <span className="shrink-0 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)]">
                                {t(`mappers.valueTypes.${source.value_type}`)}
                              </span>
                              {selected ? (
                                <svg
                                  aria-hidden
                                  className="size-4 shrink-0"
                                  fill="none"
                                  viewBox="0 0 16 16"
                                >
                                  <path
                                    d="m3.5 8.5 3 3 6-7"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                  />
                                </svg>
                              ) : null}
                            </button>
                          )
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                      {t('mappers.sourcePicker.empty')}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
